"use client";

import { memo } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/src/lib/utils";
import { ChekIcon, CloseIcon, AlertIcon } from "@/components/icons";
import type { RiskData, RiskLevel } from "@/types/endpoints/reports/GetAiReportById";
import { RISK_STYLES, RISK_COLORS } from "../../constants/riskConfig";

interface RiskIndicatorProps {
  risk: RiskData;
  className?: string;
}

// Re-export для обратной совместимости
export const riskColors = RISK_COLORS;

const RiskIndicator = memo(function RiskIndicator({ risk, className }: RiskIndicatorProps) {
  const t = useTranslations();

  const getRiskConfig = (level: RiskLevel) => {
    switch (level) {
      case "low":
        return {
          label: t("label-risk-low"),
          icon: <ChekIcon size={16} className="h-4 w-4 text-green-600" />,
          message: t("label-risk-message-low"),
          color: RISK_COLORS.low,
        };
      case "medium":
        return {
          label: t("label-risk-medium"),
          icon: <AlertIcon size={16} className="h-4 w-4 text-yellow-600" />,
          message: t("label-risk-message-medium"),
          color: RISK_COLORS.medium,
        };
      case "high":
        return {
          label: t("label-risk-high"),
          icon: <CloseIcon size={16} className="h-4 w-4 text-red-600" />,
          message: t("label-risk-message-high"),
          color: RISK_COLORS.high,
        };
      case "critical":
        return {
          label: t("label-risk-critical"),
          icon: <CloseIcon size={16} className="h-4 w-4 text-red-600" />,
          message: t("label-risk-message-critical"),
          color: RISK_COLORS.critical,
        };
    }
  };

  const config = getRiskConfig(risk.level);
  const styles = RISK_STYLES[risk.level];

  return (
    <div
      className={cn(
        "flex items-center justify-between rounded-md px-3 py-2 border",
        styles.bg,
        styles.border,
        styles.text,
        className
      )}
    >
      <div className="flex items-center gap-2">
        {config.icon}
        <span className="font-medium">{config.label}</span>
      </div>
      <span className="text-xs text-muted-foreground">{config.message}</span>
    </div>
  );
});

export default RiskIndicator;